import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useWorkspace } from 'hooks/useWorkspace';
import cn from 'classnames';

import { categorizeFeedback, sortFeedbackItems } from 'utlis/categorizeFeedback';
import { DEMO_VIDEO, TEMPLATE_TITLES } from 'utlis/constants';
import { buildDisplayNumberMap } from 'utlis/feedbackNumbering';
import { prefetchImagesAsBlobs } from 'utlis/image';

import { EFFORT_GROUPS, IMPACT_GROUPS, SORT_MODES } from './constants';
import { FeedbackList } from './FeedbackList';
import ImageCarousel from './ImageCarousel';
import PageVariantPanel from './PageVariantPanel';
import { PrototypePreviewProvider } from './PrototypePreviewContext';

import styles from './styles.module.scss';

// Templates that support page-level variants (Generate variant / prototype in the carousel)
const PAGE_VARIANT_TEMPLATES = [
  TEMPLATE_TITLES.UX_CRITIQUE,
  TEMPLATE_TITLES.GROWTH_AUDIT,
];

const CategoryDetailViewContent = ({
  category,
  isPublicView = false,
  onCategoryClick: _onCategoryClick,
}) => {
  const currentRecord = useSelector(state => state.historyReducer.currentRecord);
  const authUser = useSelector(state => state.userStore);
  const { isViewer } = useWorkspace();
  const isReviewOwner = !isViewer && Boolean(
    authUser?.uuid && currentRecord?.owner?.uuid === authUser.uuid
  );
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedFeedbackIndex, setSelectedFeedbackIndex] = useState(null);
  const [imagesLoading, setImagesLoading] = useState(false);
  const userClickSupersedesScrollRef = useRef(false);

  const reviewType = currentRecord?.extra_data?.template ?? currentRecord?.template;

  const [prefetchedImages, setPrefetchedImages] = useState([]);

  const allFeedbackItems = useMemo(
    () => {
      const deeperInsights = currentRecord?.extra_data?.post_processes?.deeper_insights;
      const hasExtraDataInsights = deeperInsights?.status === 2
        && deeperInsights?.feedback?.length > 0;

      const rawFeedback = currentRecord?.final_feedback || [];

      // When extra_data provides deeper insights, filter them out of final_feedback
      // to avoid duplicates (extra_data version takes precedence)
      const filteredFeedback = hasExtraDataInsights
        ? rawFeedback.filter(item => item.category !== 'deeper_insight' && !item.isDeeperInsight)
        : rawFeedback;

      const finalFeedback = filteredFeedback.map((item, index) => ({
        ...item,
        id: item.id || index,
        index,
      }));

      if (hasExtraDataInsights) {
        const deeperInsightItems = deeperInsights.feedback.map((insight, idx) => ({
          ...insight,
          id: insight.id || `deeper-insight-${idx}`,
          index: finalFeedback.length + idx,
          isDeeperInsight: true,
        }));

        return [...finalFeedback, ...deeperInsightItems];
      }

      return finalFeedback;
    },
    [currentRecord]
  );

  const [sortMode, setSortMode] = useState(SORT_MODES.IMPACT);

  const sortedFeedback = useMemo(
    () => sortFeedbackItems(allFeedbackItems, sortMode, IMPACT_GROUPS, EFFORT_GROUPS),
    [allFeedbackItems, sortMode]
  );

  const displayNumberMap = useMemo(
    () => buildDisplayNumberMap(sortedFeedback),
    [sortedFeedback]
  );

  const categorizedFeedback = useMemo(
    () => {
      const result = categorizeFeedback(currentRecord?.final_feedback || [], reviewType, currentRecord);

      return result;
    },
    [currentRecord, reviewType]
  );

  // Transform design images to proper format using current record's frame_images
  const rawImageUrls = useMemo(() => {
    if (!currentRecord?.extra_data?.frame_images || !Array.isArray(currentRecord.extra_data.frame_images)) {
      return [];
    }

    const prefixUrl = process.env.REACT_APP_FIREBASE_STORAGE_PREFIX;
    const postfixUrl = '?alt=media';

    return currentRecord.extra_data.frame_images.map(url => `${prefixUrl}${encodeURIComponent(url)}${postfixUrl}`);
  }, [currentRecord?.extra_data?.frame_images]);

  // Prefetch all images when rawImageUrls change
  useEffect(() => {
    if (rawImageUrls.length === 0) {
      setPrefetchedImages([]);
      setImagesLoading(false);

      return;
    }

    const prefetchImages = async () => {
      setImagesLoading(true);

      try {
        const prefetched = await prefetchImagesAsBlobs(rawImageUrls);

        setPrefetchedImages(prefetched);
      }
      catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to prefetch images:', error);

        // Fallback to original URLs
        const fallbackImages = rawImageUrls.map((url, index) => ({
          originalUrl: url,
          blobUrl: url,
          id: index,
          page: index + 1,
          alt: `Design image ${index + 1}`,
        }));

        setPrefetchedImages(fallbackImages);
      }
      finally {
        setImagesLoading(false);
      }
    };

    prefetchImages();
  }, [rawImageUrls]);

  // Transform prefetched images to the format expected by ImageCarousel
  const formattedImages = useMemo(() => prefetchedImages.map((image, index) => ({
    id: index,
    url: image.blobUrl, // Use the prefetched blob URL instead of original URL
    page: index + 1, // Assuming 1-indexed pages
    alt: `Design image ${index + 1}`,
  })), [prefetchedImages]);

  // Open on the first visible findings card and the screen that contains it
  useEffect(() => {
    const firstVisibleItem = sortedFeedback.find(entry => {
      if (entry.type !== 'item') {
        return false;
      }

      if (isPublicView && entry.data?.dismiss) {
        return false;
      }

      return true;
    })?.data;

    // Prevent the list observer from snapping to source-order item 0 on mount
    userClickSupersedesScrollRef.current = true;

    if (typeof firstVisibleItem?.index === 'number') {
      setSelectedFeedbackIndex(firstVisibleItem.index);
    }
    else {
      setSelectedFeedbackIndex(null);
    }

    const desiredIndex = firstVisibleItem?.page ? firstVisibleItem.page - 1 : 0;
    const clampedIndex = formattedImages.length > 0
      ? Math.min(Math.max(0, desiredIndex), formattedImages.length - 1)
      : Math.max(0, desiredIndex);

    setSelectedImageIndex(clampedIndex);
    // Re-run only when opening a review, not when a finding is liked or dismissed
  }, [category, currentRecord?.uuid]);

  const handleSortModeChange = useCallback(mode => {
    userClickSupersedesScrollRef.current = true;
    setSortMode(mode);

    const nextSorted = sortFeedbackItems(allFeedbackItems, mode, IMPACT_GROUPS, EFFORT_GROUPS);
    const firstItem = nextSorted.find(entry => entry.type === 'item')?.data;

    if (!firstItem) {
      setSelectedFeedbackIndex(null);

      return;
    }

    const cardIndex = typeof firstItem.index === 'number'
      ? firstItem.index
      : allFeedbackItems.findIndex(item => item === firstItem);

    if (cardIndex >= 0) {
      setSelectedFeedbackIndex(cardIndex);
    }

    if (firstItem.page) {
      const imageIndex = firstItem.page - 1;

      if (imageIndex >= 0 && imageIndex < formattedImages.length) {
        setSelectedImageIndex(imageIndex);
      }
    }
  }, [allFeedbackItems, formattedImages.length]);

  const handleImageIndexChange = useCallback(
    index => {
      userClickSupersedesScrollRef.current = true;
      setSelectedImageIndex(index);
    },
    []
  );

  const handleInspectFeedback = useCallback(
    feedbackData => {
      if (!feedbackData.page) {
        return;
      }

      userClickSupersedesScrollRef.current = true;

      const cardIndex = allFeedbackItems.findIndex(item => item === feedbackData);

      if (cardIndex >= 0) {
        setSelectedFeedbackIndex(cardIndex);
      }

      // Convert page number to 0-based index for the carousel
      const imageIndex = feedbackData.page - 1;

      // Validate that the page is within the valid range of images
      if (imageIndex < 0 || imageIndex >= formattedImages.length) {
        return;
      }

      // Directly update the selected image index
      setSelectedImageIndex(imageIndex);
    },
    [allFeedbackItems, formattedImages.length]
  );

  const handlePinClick = useCallback(
    feedbackIndex => {
      userClickSupersedesScrollRef.current = true;

      if (typeof feedbackIndex === 'number' && feedbackIndex >= 0) {
        setSelectedFeedbackIndex(feedbackIndex);
      }
    },
    []
  );

  // show protypes and varient of current feedback card only
  const selectedFeedbackItem = selectedFeedbackIndex != null
    ? allFeedbackItems[selectedFeedbackIndex]
    : null;

  const currentPage = formattedImages[selectedImageIndex]?.page ?? selectedImageIndex + 1;
  const supportsPageVariants = PAGE_VARIANT_TEMPLATES.includes(reviewType);

  const isSelectedDeeperInsight = selectedFeedbackItem?.isDeeperInsight
    || selectedFeedbackItem?.category === 'deeper-insights';

  const activeInsightUuid = (() => {
    if (isSelectedDeeperInsight && selectedFeedbackItem?.uuid != null) {
      return String(selectedFeedbackItem.uuid);
    }

    if (supportsPageVariants) {
      return `page_${currentPage}`;
    }

    return selectedFeedbackItem?.uuid != null
      ? String(selectedFeedbackItem.uuid)
      : null;
  })();

  const pageEligibleFeedback = useMemo(() => {
    if (!supportsPageVariants) {
      return [];
    }

    return (currentRecord?.final_feedback || []).filter(item => (
      item?.page === currentPage
      && item?.dismiss !== true
      && !item?.isDeeperInsight
      && item?.category !== 'deeper_insight'
      && item?.category !== 'positive'
    ));
  }, [supportsPageVariants, currentRecord, currentPage]);

  return (
    <>
      <div
        className={cn(
          'mx-8',
          styles['detail-preview--canvas'],
          styles.createReviewLeftPanel
        )}
      >
        <ImageCarousel
          feedback={allFeedbackItems}
          displayNumberMap={displayNumberMap}
          images={formattedImages}
          selectedImageIndex={selectedImageIndex}
          onImageIndexChange={handleImageIndexChange}
          onPinClick={handlePinClick}
          isLoading={imagesLoading}
          activeInsightUuid={activeInsightUuid}
          isReviewOwner={isReviewOwner}
        />
      </div>

      {supportsPageVariants && pageEligibleFeedback.length > 0 && (
        <PageVariantPanel
          key={`page-variant-${currentPage}`}
          page={currentPage}
          eligibleFeedback={pageEligibleFeedback}
          designReviewUuid={currentRecord?.uuid}
          isPublicView={isPublicView}
        />
      )}

      <FeedbackList
        feedback={allFeedbackItems}
        sortedFeedback={sortedFeedback}
        displayNumberMap={displayNumberMap}
        sortMode={sortMode}
        onSortModeChange={handleSortModeChange}
        categoryName={getCategoryName()}
        watchDemo={watchDemo}
        isPublicView={isPublicView}
        onInspectFeedback={handleInspectFeedback}
        totalImages={formattedImages.length}
        userClickSupersedesScrollRef={userClickSupersedesScrollRef}
        selectedFeedbackIndex={selectedFeedbackIndex}
      />
    </>
  );

  function watchDemo() {
    window.open(DEMO_VIDEO.URL, '_blank');
  }

  function getCategoryName() {
    if (currentRecord?.extra_data?.template === TEMPLATE_TITLES.EDGE_CASES) {
      return currentRecord.extra_data?.review_frame_name || 'Review Frame';
    }

    const categoryData = categorizedFeedback.find(
      ({ categoryKey }) => categoryKey === category
    );

    return categoryData?.categoryName || currentRecord?.extra_data?.review_frame_name;
  }
};

CategoryDetailViewContent.propTypes = {
  category: PropTypes.string.isRequired,
  isPublicView: PropTypes.bool,
  onCategoryClick: PropTypes.func.isRequired,
};

CategoryDetailViewContent.defaultProps = {
  feedbackData: [],
  className: '',
};

// Wrap with PrototypePreviewProvider so the context is available to both
// ImageCarousel (left panel) and DesignInspirationPanel (right panel feedback cards).
// eslint-disable-next-line react/no-multi-comp
const CategoryDetailView = props => (
  <PrototypePreviewProvider>
    <CategoryDetailViewContent {...props} />
  </PrototypePreviewProvider>
);

export default CategoryDetailView;
